import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MapPin, Menu, X, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { businessInfo } from "../data/mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { href: "/#services", label: "Services" },
    { href: "/#specials", label: "Specials" },
    { href: "/#reviews", label: "Reviews" },
    { href: "/#service-areas", label: "Service Areas" },
    { href: "/blog", label: "Blog", isRoute: true },
    { href: "/#faq", label: "FAQ" },
    { href: "/#contact", label: "Contact" }
  ];

  return (
    <>
      {/* Top offer strip */}
      <div className="bg-green-600 text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <span className="font-semibold tracking-wide">$7 OFF Any Smog Check — Mention this site at checkout</span>
          <a href={`tel:${businessInfo.phoneRaw}`} className="hidden sm:flex items-center gap-2 hover:underline">
            <Phone className="w-3.5 h-3.5" />
            {businessInfo.phone}
          </a>
        </div>
      </div>

      <header
        className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm" : "bg-white border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logo.png"
              alt="Hamilton's Smog logo"
              className="w-11 h-11 rounded-full shadow-md group-hover:scale-105 transition-transform"
            />
            <div className="leading-tight">
              <div className="font-black text-slate-900 text-sm sm:text-base tracking-tight">HAMILTON'S SMOG</div>
              <div className="text-[10px] sm:text-xs text-slate-500 font-medium">1032 First St, Novato, CA 94945</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) =>
              l.isRoute ? (
                <Link key={l.href} to={l.href} className="text-sm font-semibold text-slate-700 hover:text-green-600 transition-colors">
                  {l.label}
                </Link>
              ) : (
                <a key={l.href} href={l.href} className="text-sm font-semibold text-slate-700 hover:text-green-600 transition-colors">
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${businessInfo.phoneRaw}`} className="flex items-center gap-2 text-slate-900 font-bold hover:text-green-600">
              <Phone className="w-4 h-4" />
              {businessInfo.phone}
            </a>
            <Button asChild className="bg-green-600 hover:bg-green-700 text-white font-bold">
              <a href={businessInfo.bookingUrl} target="_blank" rel="noreferrer">
                <Calendar className="w-4 h-4 mr-2" /> Book Now
              </a>
            </Button>
          </div>

          <button className="lg:hidden p-2 text-slate-800" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
              {links.map((l) =>
                l.isRoute ? (
                  <Link key={l.href} to={l.href} className="py-2 text-slate-800 font-semibold">{l.label}</Link>
                ) : (
                  <a key={l.href} href={l.href} className="py-2 text-slate-800 font-semibold">{l.label}</a>
                )
              )}
              <div className="flex gap-2 pt-2">
                <Button asChild variant="outline" className="flex-1 border-slate-300">
                  <a href={`tel:${businessInfo.phoneRaw}`}>
                    <Phone className="w-4 h-4 mr-2" /> Call
                  </a>
                </Button>
                <Button asChild className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold">
                  <a href={businessInfo.bookingUrl} target="_blank" rel="noreferrer">Book Now</a>
                </Button>
              </div>
              <a href={businessInfo.directionsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-600 pt-1">
                <MapPin className="w-4 h-4" /> {businessInfo.address}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
